/**
 * Status → tone mapping — the single source of truth (DESIGN-SYSTEM §1.2).
 *
 * All status colors across the 5 audited domains collapse into ONE map, consumed
 * only via `<StatusBadge>` / the `tone` prop. No more inline `text-success` or
 * `●` glyphs. Unknown (domain, value) pairs fall back to "neutral".
 */

export type Tone =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  | "accent";

/**
 * Per-domain value→tone tables. A `"*"` key (none defined yet) could act as a
 * domain default; today every miss returns "neutral" via {@link statusToTone}.
 */
const MAP: Record<string, Record<string, Tone>> = {
  account: {
    active: "success",
    flood_wait: "warning",
    warming_up: "warning",
    paused: "warning",
    banned: "danger",
    auth_required: "danger",
    frozen: "danger",
    proxy_dead: "danger",
    permanent_peer_flood: "danger",
    is_premium: "accent",
    default: "neutral",
  },
  channel: {
    done: "success",
    queued: "warning",
    parsing: "warning",
    skipped: "warning",
    failed: "danger",
    failed_private: "danger",
    // Waiting on a channel admin to approve our join request — in flight, not
    // a failure, so it reads as informational rather than a warning.
    pending_join: "info",
    // Parked until the account budget resets — pending work, not a problem.
    deferred: "info",
    source: "info",
    skipped_no_users: "neutral",
    default: "neutral",
  },
  campaign: {
    done: "success",
    running: "success",
    queued: "warning",
    paused: "warning",
    cancelled: "danger",
    failed: "danger",
    draft: "neutral",
    default: "neutral",
  },
  credential: {
    healthy: "success",
    burned: "danger",
    default: "neutral",
  },
  team: {
    active: "success",
    pending: "warning",
    disabled: "danger",
    default: "neutral",
  },
  telemetr: {
    ok: "success",
    failed: "danger",
    default: "neutral",
  },
  scrape_job: {
    done: "success",
    failed: "danger",
    cancelled: "danger",
    default: "neutral",
  },
  proxy: {
    alive: "success",
    dead: "danger",
    unknown: "warning",
    default: "neutral",
  },
  user_flag: {
    premium: "accent",
    bot: "neutral",
    default: "neutral",
  },
  throttle: {
    override: "warning",
    default: "neutral",
  },
  // A broadcast into group chats; "waiting" = running, but held by something.
  broadcast: {
    active: "success",
    waiting: "info",
    paused: "warning",
    default: "neutral",
  },
  // One account's place in one chat of a broadcast.
  membership: {
    joined: "success",
    pending: "info",
    requested: "info",
    blocked: "danger",
    default: "neutral",
  },
  // A channel the aggregator fills: "stopped" = it cannot publish until a
  // person fixes something; "idle" = switched on, with nothing to read yet.
  aggregator: {
    working: "success",
    idle: "info",
    paused: "warning",
    stopped: "danger",
    default: "neutral",
  },
  // A group or channel the company runs: found by anybody, or by link only.
  own_chat: {
    open: "success",
    closed: "neutral",
    default: "neutral",
  },
  // The state of a lead in the inbox (LEAD_STATUSES in lib/inbox.ts).
  lead: {
    new: "accent",
    interested: "info",
    follow_up: "warning",
    client: "success",
    not_interested: "danger",
    closed: "neutral",
    default: "neutral",
  },
};

/**
 * Resolve a (domain, value) status to a semantic {@link Tone}.
 * Unknown domain or value → "neutral".
 */
export function statusToTone(domain: string, value: string): Tone {
  return MAP[domain]?.[value] ?? "neutral";
}
